import React, { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime } from "../lib/timezone";
import { TIMEZONES, DEFAULT_TZ } from "../lib/timezone";
import { ROLE_LABEL } from "../lib/access";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Building2, ShieldAlert, Users, Loader2, ExternalLink, Trash2, AlertTriangle, Sparkles, Plus, CheckCircle2, Mail } from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

function StatusPill({ active }) {
  return active ? (
    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
  ) : (
    <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200">Inactive</Badge>
  );
}

export default function Organizations() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/organizations").then((r) => setOrgs(r.data)).catch((e) => toast.error(formatError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openOrg = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    setConfirmName("");
    try {
      const { data } = await api.get(`/admin/organizations/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const patchOrg = async (patch) => {
    if (!detail?.id) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/organizations/${detail.id}`, patch);
      setDetail({ ...detail, ...data });
      toast.success("Organization updated");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteOrg = async () => {
    if (!detail?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/organizations/${detail.id}`, { data: { confirm_name: confirmName.trim() } });
      toast.success(`"${detail.name}" permanently deleted`);
      setDetail(null);
      setConfirmName("");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const openCleanup = async () => {
    setCleanupOpen(true);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/admin/test-orgs");
      setPreview(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setPreviewLoading(false); }
  };

  const runPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/test-orgs/purge", { confirm: true });
      toast.success(`Removed ${data.deleted_count} test organization${data.deleted_count === 1 ? "" : "s"}`);
      setCleanupOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setPurging(false); }
  };

  const emptyCreate = { org_name: "", timezone: DEFAULT_TZ, admin_name: "", admin_email: "", pilot_feedback_enabled: false };
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [resending, setResending] = useState(false);

  const resendOnboarding = async (orgId) => {
    setResending(true);
    try {
      const { data } = await api.post(`/admin/organizations/${orgId}/resend-onboarding`);
      toast.success(`Onboarding email sent to ${data.admin_email}`);
      if (created && created.id === orgId) {
        setCreated({ ...created, onboarding_email: { sent: true, message: "Onboarding email sent." } });
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  const openCreate = () => { setCreateForm(emptyCreate); setCreated(null); setCreateOpen(true); };

  const submitCreate = async () => {
    if (!createForm.org_name.trim() || !createForm.admin_name.trim() || !createForm.admin_email.trim()) {
      toast.error("Organization name, admin name, and admin email are required.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/organizations", createForm);
      setCreated(data);
      toast.success(`Organization "${data.name}" created`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <div className="animate-fade-up space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">System Owner · Internal</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Organizations</h1>
          <p className="mt-1 text-sm text-slate-500">All WatchRelay organizations. Internal administrative view for platform support only.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="cleanup-test-orgs-button" variant="outline" onClick={openCleanup} className="gap-2 text-slate-700 border-slate-300 hover:bg-slate-50">
            <Sparkles className="h-4 w-4" /> Clean up test orgs
          </Button>
          <Button data-testid="create-org-button" onClick={openCreate} className="gap-2 bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Create organization
          </Button>
        </div>
      </div>

      {/* Explicit internal-access banner */}
      <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800" data-testid="internal-access-banner">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
        <span>
          <span className="font-semibold">Internal administrative access.</span>{" "}
          Cross-organization data shown here is restricted to the WatchRelay System Owner for legitimate
          platform administration and support. Every organization you open is recorded in the audit log.
        </span>
      </div>

      <Card className="rounded-md border border-slate-200 bg-white p-0 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading organizations…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="organizations-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3">Organization</th>
                  <th className="px-5 py-3">Pilot</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Users</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} data-testid={`org-row-${o.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{o.name}</span>
                        {o.is_demo && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">Demo</Badge>}
                        {o.is_internal && <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">Internal</Badge>}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {o.pilot_feedback_enabled
                        ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Pilot on</Badge>
                        : <span className="text-slate-400">Off</span>}
                    </td>
                    <td className="px-5 py-3"><StatusPill active={o.active} /></td>
                    <td className="px-5 py-3 text-slate-700"><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-400" />{o.user_count}</span></td>
                    <td className="px-5 py-3 text-slate-500">{o.created_at ? fmtDateTime(o.created_at, user?.org_timezone) : "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <Button data-testid={`org-open-${o.id}`} variant="outline" size="sm" className="gap-1 text-blue-700 border-blue-200 hover:bg-blue-50" onClick={() => openOrg(o.id)}>
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                      </Button>
                    </td>
                  </tr>
                ))}
                {orgs.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">No organizations found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create organization dialog (System Owner manual onboarding) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="create-org-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-blue-600" /> Create organization</DialogTitle>
            <DialogDescription>Onboard a new customer organization and assign its first admin.</DialogDescription>
          </DialogHeader>

          {created ? (
            <div className="space-y-4" data-testid="create-org-success">
              <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span><span className="font-semibold">{created.name}</span> is ready and <span className="font-semibold">{created.admin?.name}</span> ({created.admin?.email}) was added as its admin.</span>
              </div>
              <div className={`rounded-md border p-3 text-sm ${created.onboarding_email?.sent ? "border-slate-200" : "border-amber-300 bg-amber-50"}`}>
                {created.onboarding_email?.sent ? (
                  <p className="flex items-start gap-2 text-slate-700" data-testid="onboarding-sent">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                    We emailed a secure “Set your password” link to {created.admin?.email}. It expires in 60 minutes. No password is sent by email.
                  </p>
                ) : (
                  <div data-testid="onboarding-failed">
                    <p className="flex items-start gap-2 text-amber-800">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                      {created.onboarding_email?.message || "The onboarding email failed to send."}
                    </p>
                    <Button data-testid="resend-onboarding-success" size="sm" disabled={resending} onClick={() => resendOnboarding(created.id)} className="mt-2 gap-1 bg-blue-600 text-white hover:bg-blue-700">
                      <Mail className="h-3.5 w-3.5" /> {resending ? "Resending…" : "Resend onboarding email"}
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={openCreate}>Create another</Button>
                <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => setCreateOpen(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className={labelCls}>Organization name</Label>
                <Input data-testid="create-org-name" value={createForm.org_name} onChange={(e) => setCreateForm({ ...createForm, org_name: e.target.value })} className="mt-1.5" placeholder="e.g. Cedar County Sheriff's Office" />
              </div>
              <div>
                <Label className={labelCls}>Timezone</Label>
                <Select value={createForm.timezone} onValueChange={(v) => setCreateForm({ ...createForm, timezone: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="create-org-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((t) => <SelectItem key={t.tz} value={t.tz}>{t.label} ({t.tz})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className={labelCls}>Admin full name</Label>
                  <Input data-testid="create-org-admin-name" value={createForm.admin_name} onChange={(e) => setCreateForm({ ...createForm, admin_name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className={labelCls}>Admin email</Label>
                  <Input data-testid="create-org-admin-email" type="email" value={createForm.admin_email} onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <label className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <span className="text-sm font-medium text-slate-700">Enable Pilot Feedback for this org</span>
                <Switch data-testid="create-org-pilot" checked={createForm.pilot_feedback_enabled} onCheckedChange={(v) => setCreateForm({ ...createForm, pilot_feedback_enabled: v })} />
              </label>
              <p className="text-xs text-slate-400">The administrator will receive an email with a secure link to set their password. No temporary password is created or shown.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
                <Button data-testid="create-org-submit" onClick={submitCreate} disabled={creating} className="gap-1 bg-blue-600 text-white hover:bg-blue-700">
                  {creating ? "Creating…" : "Create organization"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cleanup test orgs dialog */}
      <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" /> Clean up test organizations</DialogTitle>
            <DialogDescription>
              Permanently removes automation/QA organizations whose members all use disposable
              test email domains. Real, demo, pilot, and internal organizations are never touched.
            </DialogDescription>
          </DialogHeader>

          {previewLoading || !preview ? (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Scanning…</div>
          ) : preview.count === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="cleanup-empty">No test organizations found — nothing to clean up. 🎉</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                This will permanently delete <span className="font-bold">{preview.count}</span> test organization{preview.count === 1 ? "" : "s"} and all their data. This cannot be undone.
              </div>
              <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-slate-100 p-2" data-testid="cleanup-list">
                {preview.organizations.map((o) => (
                  <div key={o.id} className="flex items-center justify-between px-2 py-1 text-sm">
                    <span className="text-slate-700">{o.name}</span>
                    <span className="text-xs text-slate-400">{o.user_count} user{o.user_count === 1 ? "" : "s"}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCleanupOpen(false)} disabled={purging}>Cancel</Button>
                <Button data-testid="cleanup-confirm-button" onClick={runPurge} disabled={purging} className="gap-1 bg-red-600 text-white hover:bg-red-700">
                  <Trash2 className="h-3.5 w-3.5" /> {purging ? "Deleting…" : `Delete ${preview.count} test org${preview.count === 1 ? "" : "s"}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail / manage dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="org-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" /> {detail?.name || "Organization"}</DialogTitle>
            <DialogDescription>Internal administrative access — you are not a member of this organization.</DialogDescription>
          </DialogHeader>

          {detailLoading || !detail?.members ? (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md border border-slate-100 p-3"><p className="text-2xl font-black text-slate-900">{detail.user_count}</p><p className={labelCls}>Users</p></div>
                <div className="rounded-md border border-slate-100 p-3"><p className="text-2xl font-black text-slate-900">{detail.handoff_count}</p><p className={labelCls}>Handoffs</p></div>
                <div className="rounded-md border border-slate-100 p-3"><p className="text-2xl font-black text-slate-900">{(detail.divisions || []).length}</p><p className={labelCls}>Divisions</p></div>
              </div>

              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="resend-onboarding-row">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Onboarding email</p>
                  <p className="text-xs text-slate-500">Resend the secure “Set your password” link to this org's admin (expires in 60 min).</p>
                </div>
                <Button data-testid="resend-onboarding-detail" variant="outline" size="sm" disabled={resending} onClick={() => resendOnboarding(detail.id)} className="gap-1 text-blue-700 border-blue-200 hover:bg-blue-50">
                  <Mail className="h-3.5 w-3.5" /> {resending ? "Sending…" : "Resend"}
                </Button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Pilot Feedback</p>
                    <p className="text-xs text-slate-500">Enable the in-app feedback tools for this organization.</p>
                  </div>
                  <Switch data-testid="org-toggle-pilot" disabled={saving} checked={!!detail.pilot_feedback_enabled} onCheckedChange={(v) => patchOrg({ pilot_feedback_enabled: v })} />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Active organization</p>
                    <p className="text-xs text-slate-500">Mark this organization active or inactive for administrative tracking.</p>
                  </div>
                  <Switch data-testid="org-toggle-active" disabled={saving} checked={detail.active !== false} onCheckedChange={(v) => patchOrg({ active: v })} />
                </div>
              </div>

              <div>
                <Label className={labelCls}>Members ({(detail.members || []).length})</Label>
                <div className="mt-2 space-y-1.5" data-testid="org-members-list">
                  {(detail.members || []).map((m) => (
                    <div key={m.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{m.name} {m.active === false && <span className="ml-1 text-[10px] uppercase text-slate-400">Inactive</span>}</p>
                        <p className="text-xs text-slate-500">{m.email} · {m.primary_division || "—"}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">{ROLE_LABEL[m.role] || m.role}</Badge>
                    </div>
                  ))}
                  {(detail.members || []).length === 0 && <p className="text-sm text-slate-400">No members.</p>}
                </div>
              </div>

              <p className="text-xs text-slate-400">Timezone: {detail.timezone} · Created {detail.created_at ? fmtDateTime(detail.created_at, user?.org_timezone) : "—"}</p>

              {/* Lifecycle — Danger zone */}
              <div className="rounded-md border border-red-200 bg-red-50/40 p-4" data-testid="org-danger-zone">
                <p className="flex items-center gap-1.5 text-sm font-bold text-red-700"><AlertTriangle className="h-4 w-4" /> Lifecycle controls</p>

                <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                  <p className="text-sm font-semibold text-slate-800">Deactivate organization</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {detail.active
                      ? "Currently active. Use the “Active organization” toggle above to deactivate — this blocks login and access for all its users while preserving every user, handoff, schedule, record, feedback item, and setting."
                      : "This organization is deactivated. Its users cannot log in, but all data is preserved. Re-enable it with the “Active organization” toggle above."}
                  </p>
                </div>

                <div className="mt-3 rounded-md border border-red-200 bg-white p-3">
                  <p className="text-sm font-semibold text-red-700">Permanently delete organization</p>
                  {detail.is_internal ? (
                    <p className="mt-1 flex items-start gap-1 text-xs text-slate-600" data-testid="org-delete-blocked-internal">
                      <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
                      The WatchRelay (Internal) organization cannot be deleted.
                    </p>
                  ) : (
                    <>
                      <p className="mt-1 text-xs text-slate-600">
                        This permanently deletes the organization and <span className="font-semibold">all of its data</span> — users, handoffs, schedules, history, comments, feedback, and configuration. This cannot be undone.
                      </p>
                      {detail.has_substantial_activity && (
                        <p className="mt-2 flex items-start gap-1 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="org-delete-activity-warning">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                          This organization has real activity ({detail.user_count} users, {detail.handoff_count} handoffs, {detail.feedback_count} feedback items). Consider <span className="font-semibold">deactivating</span> instead to preserve records.
                        </p>
                      )}
                      <Label className="mt-3 block text-xs font-semibold text-slate-600">Type the organization name to confirm: <span className="font-mono text-slate-900">{detail.name}</span></Label>
                      <Input
                        data-testid="org-delete-confirm-input"
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        placeholder={detail.name}
                        className="mt-1.5"
                      />
                      <Button
                        data-testid="org-delete-button"
                        disabled={deleting || confirmName.trim() !== detail.name}
                        onClick={deleteOrg}
                        className="mt-2 gap-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Deleting…" : "Permanently delete organization"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
